

import { useTranslation } from '@fluxora/i18n';
import { Input, Label, Spinner } from '@fluxora/ui';
import type { ServiceableZip } from '@rheora/enrollment-contract';
import { useEffect, useId, useMemo, useState } from 'react';

import { useServiceableZipsQuery } from '../api/serviceability.queries';

/**
 * ZIP entry as a SEARCHABLE combobox over the serviceable city+ZIP list.
 *
 * ── WHY THIS REPLACED A PLAIN NUMERIC INPUT ────────────────────────────────
 * The bare input required the shopper to already KNOW a ZIP we serve — there was
 * no way to discover one from the UI. In testing that meant the same two or three
 * remembered ZIPs were walked over and over, which burns their premises fast
 * (Quotation locks an ESI ID the moment a DRAFT quote exists), while the other
 * seventeen city+ZIP pairs went untouched.
 *
 * ── TYPING STILL WORKS, AND FILTERS ON CITY TOO ────────────────────────────
 * It is a text input first and a listbox second, exactly like
 * `AddressAutocomplete`. Digits filter by ZIP; letters filter by CITY, so
 * "abil" finds Abilene's two ZIPs without the shopper knowing either. A ZIP that
 * is not in the list can still be typed in full and submitted — the dropdown is
 * an aid, never a gate, and serviceability is decided by the BFF regardless.
 *
 * ── NO DEBOUNCE, DELIBERATELY ──────────────────────────────────────────────
 * Unlike `AddressAutocomplete`, filtering here is CLIENT-side over a list that is
 * fetched once and cached for the session (~20 rows). There is no per-keystroke
 * request to debounce, so adding a delay would only make it feel laggy.
 */
export interface ZipComboboxProps {
  /** Element id for the input (label association + tests). */
  readonly id: string;
  /** Already-translated field label. */
  readonly label: string;
  /** Current ZIP value — owned by the funnel store, so a tab-switch retains it. */
  readonly value: string;
  /** Raise every change (typed or picked) so the parent stays the source of truth. */
  readonly onValueChange: (zip: string) => void;
  /** Gate the fetch on the funnel session — the BFF route is session-gated. */
  readonly sessionReady: boolean;
  readonly describedBy?: string;
}

/** Show every ZIP until the shopper narrows it; 20 rows fits the scroll box. */
function filterZips(zips: readonly ServiceableZip[], raw: string): readonly ServiceableZip[] {
  const q = raw.trim().toLowerCase();
  if (q.length === 0) return zips;
  return zips.filter((z) => z.zip.startsWith(q) || z.city.toLowerCase().includes(q));
}

export function ZipCombobox({
  id,
  label,
  value,
  onValueChange,
  sessionReady,
  describedBy,
}: ZipComboboxProps): JSX.Element {
  const { t } = useTranslation();
  const listboxId = useId();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const query = useServiceableZipsQuery(sessionReady);
  const zips = useMemo(() => query.data?.zips ?? [], [query.data]);
  const filtered = useMemo(() => filterZips(zips, value), [zips, value]);

  // Clamp the highlight if the list shrank under it as the shopper typed.
  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(filtered.length - 1);
  }, [filtered.length, activeIndex]);

  const optionId = (index: number): string => `${listboxId}-opt-${index}`;

  // Keep the keyboard-highlighted option inside the scroll box. jsdom does not
  // implement scrollIntoView, so feature-detect rather than call it blindly —
  // otherwise every keyboard test dies in the effect instead of asserting.
  useEffect(() => {
    if (activeIndex < 0) return;
    const option = document.getElementById(`${listboxId}-opt-${activeIndex}`);
    if (option !== null && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, listboxId]);

  const showList = open && !query.isError;

  const pick = (zip: ServiceableZip): void => {
    onValueChange(zip.zip);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // Accept letters WHILE SEARCHING (so "houston" filters), but never let a
    // non-digit reach the parent as a ZIP — the funnel store holds a ZIP, not a
    // query string, and `isValidZip` gates the address call downstream.
    const raw = event.target.value;
    const digitsOnly = raw.replace(/\D/gu, '').slice(0, 5);
    onValueChange(/^[0-9]*$/u.test(raw.trim()) ? digitsOnly : raw);
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!showList) {
        setOpen(true);
        return;
      }
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      const active = activeIndex >= 0 ? filtered[activeIndex] : undefined;
      if (showList && active !== undefined) {
        event.preventDefault();
        pick(active);
      }
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    // ⚠️ `z-40` WHILE THE LIST IS OPEN — Azure #14553. The popup's own z-index
    // only orders it inside the nearest stacking context, and every card on the
    // landing page makes one (each carries a `transform` for its entry
    // animation). So an open list that overhung this card was painted UNDER the
    // cards below it. Lifting the card-local wrapper is what actually wins;
    // only while open, so a closed combobox never steals stacking.
    <div className={showList ? 'relative z-40 flex flex-col gap-1' : 'relative flex flex-col gap-1'}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        name="zip"
        // A combobox owns its own affordance; browser autofill would fight the
        // listbox. `inputMode` stays text — the field accepts a city search too.
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={showList && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        placeholder={t('shop.landing.zip.zip.placeholder')}
        value={value}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        // Option mousedown is prevent-defaulted below, so a pick never trips this.
        onBlur={() => setOpen(false)}
        onKeyDown={handleKeyDown}
        aria-describedby={describedBy}
      />

      {showList ? (
        /* eslint-disable-next-line jsx-a11y/no-static-element-interactions --
           This div is the popup SURFACE, not a control. Its only handler is a
           mousedown that prevent-defaults so focus does not leave the input
           before a click lands. Keyboard users never reach it: the WAI-ARIA
           combobox pattern keeps focus on the input and drives the listbox via
           aria-activedescendant (see handleKeyDown). Giving it a role or a tab
           stop would ADD a trap, not remove one. */
        <div
          /* HEIGHT: short, and SCROLLED rather than grown. `min(38vh,248px)`
             is about six rows and keeps the open list inside the card it opens
             over; the old `max-h-80` (320px) overhung it, and the overhang was
             painted behind the cards further down the page (#14553). Every ZIP
             stays reachable — the list scrolls — and `overscroll-contain` stops
             that scroll leaking into the page at either end. Matches the
             address picker's `ListboxSelect` so both lists behave alike. */
          className="absolute left-0 right-0 top-full z-10 mt-1 max-h-[min(38vh,248px)] overflow-y-auto overscroll-contain rounded-md border border-border bg-bg shadow-lg"
          onMouseDown={(event) => event.preventDefault()}
        >
          {query.isPending ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted" role="status">
              <Spinner size="sm" />
              <span>{t('shop.landing.zip.picker.loading')}</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted" role="status">
              {t('shop.landing.zip.picker.empty')}
            </div>
          ) : (
            <ul id={listboxId} role="listbox" aria-label={t('shop.landing.zip.picker.listLabel')}>
              {filtered.map((zip, index) => (
                /* eslint-disable-next-line jsx-a11y/click-events-have-key-events --
                   `role="option"` inside `role="listbox"` is the native ARIA
                   pairing, and the keyboard path is Enter on the input (see
                   handleKeyDown). A per-option key handler would require
                   focusing options, which the combobox pattern forbids. */
                <li
                  key={zip.zip}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={
                    index === activeIndex
                      ? 'cursor-pointer bg-primary/10 px-3 py-2 text-sm font-medium text-foreground'
                      : 'cursor-pointer px-3 py-2 text-sm font-medium text-foreground hover:bg-surface'
                  }
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(zip)}
                >
                  {/* ZIP ONLY. The city and the premise count used to render
                      here, and in a ~180px column the city wrapped to a second
                      line and the count truncated — a cluttered list for two
                      pieces of information the shopper did not ask for. The city
                      is still SEARCHABLE (see filterZips); it is just not shown. */}
                  {zip.zip}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
